import React, { useState, useRef } from 'react';
import { Box, Card, CardContent, Typography, Grid, IconButton, Menu, MenuItem, Divider, Stack, Tabs, Tab } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import captureScreenshot from 'utils/screenshotUtils';
import {
  TrendingUp,
  BarChart as BarChart3,
  MenuBook,
  AttachMoney,
  People as Users,
  GpsFixed as Target,
  //   Security,
  CompareArrows
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import Favicon from 'components/Favicon';

// Get icon for metric based on name
const getIconForMetric = (metricName) => {
  const name = metricName.toLowerCase();
  if (name.includes('accuracy') || name.includes('data')) {
    return <Target sx={{ fontSize: 24 }} />;
  } else if (name.includes('roi') || name.includes('revenue') || name.includes('cost')) {
    return <AttachMoney sx={{ fontSize: 24 }} />;
  } else if (name.includes('user') || name.includes('customer') || name.includes('client')) {
    return <Users sx={{ fontSize: 24 }} />;
  } else {
    return <TrendingUp sx={{ fontSize: 24 }} />;
  }
};

// Get nested property from object safely
const getNestedProperty = (obj, path) => {
  if (!obj) return null;

  const properties = path.split('.');
  let current = obj;

  for (const property of properties) {
    if (current === null || current === undefined) {
      return null;
    }
    current = current[property];
  }

  return current;
};

// Get a color for each competitor
const getCompetitorColor = (index) => {
  const colors = [
    '#3f51b5', // primary
    '#f44336', // red
    '#4caf50', // green
    '#ff9800', // orange
    '#2196f3', // blue
    '#9c27b0', // purple
    '#00bcd4', // cyan
    '#ff5722', // deep orange
    '#673ab7', // deep purple
    '#795548' // brown
  ];
  return colors[index % colors.length];
};

const CompaniesMarketTrends = ({ competitors, companyData }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const open = Boolean(anchorEl);
  const contentRef = useRef(null);

  // Filter competitors to only include those with market trends data
  const filteredCompetitors = competitors.filter((competitor) => {
    const marketTrends = getNestedProperty(competitor, 'analysis_data.market_trends');
    return marketTrends && (marketTrends.trends?.length > 0 || marketTrends.benchmarks?.length > 0);
  });

  // Check if company data has market trends
  const hasCompanyMarketTrends =
    companyData &&
    companyData.analysis_data &&
    companyData.analysis_data.market_trends &&
    (companyData.analysis_data.market_trends.trends?.length > 0 || companyData.analysis_data.market_trends.benchmarks?.length > 0);

  // Get the main company name - use service_name if available
  const mainCompanyName = companyData?.service_name || companyData?.company_name;

  // Combine company data with competitors if it has market trends
  const allCompanies = hasCompanyMarketTrends
    ? [{ ...companyData, isMainCompany: true }, ...filteredCompetitors]
    : [...filteredCompetitors];

  // Handle menu
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Export as PNG
  const downloadAsPng = () => {
    captureScreenshot({
      contentRef,
      filename: 'market-trends-comparison',
      afterCapture: handleClose
    });
  };

  // If no companies with market trends data, return null
  if (allCompanies.length === 0) return null;

  // Combine all trends and benchmarks for the "All Trends" tab
  const allTrends = [];
  const allBenchmarks = [];

  allCompanies.forEach((company) => {
    const marketTrends = getNestedProperty(company, 'analysis_data.market_trends');
    if (!marketTrends) return;

    if (marketTrends.trends && marketTrends.trends.length > 0) {
      marketTrends.trends.forEach((trend) => {
        // Check if this trend is already added (based on title)
        const existingTrendIndex = allTrends.findIndex((t) => t.trend.title === trend.title);

        if (existingTrendIndex === -1) {
          // Add new trend with company reference
          allTrends.push({
            trend,
            companies: [
              {
                name: company.isMainCompany ? mainCompanyName : company.company_name,
                companyObj: company
              }
            ]
          });
        } else {
          // Add company to existing trend
          allTrends[existingTrendIndex].companies.push({
            name: company.isMainCompany ? mainCompanyName : company.company_name,
            companyObj: company
          });
        }
      });
    }

    if (marketTrends.benchmarks && marketTrends.benchmarks.length > 0) {
      marketTrends.benchmarks.forEach((benchmark) => {
        // Check if this benchmark is already added (based on title to prevent duplicates)
        const existingBenchmarkIndex = allBenchmarks.findIndex(
          (b) => benchmark.metric_value && b.benchmark.metric_value === benchmark.metric_value
        );

        if (existingBenchmarkIndex === -1) {
          // Add new benchmark with company reference
          allBenchmarks.push({
            benchmark,
            companies: [
              {
                name: company.isMainCompany ? mainCompanyName : company.company_name,
                companyObj: company
              }
            ]
          });
        } else {
          // Add company to existing benchmark
          allBenchmarks[existingBenchmarkIndex].companies.push({
            name: company.isMainCompany ? mainCompanyName : company.company_name,
            companyObj: company
          });
        }
      });
    }
  });

  return (
    <Card
      sx={{
        width: '100%',
        mb: 3,
        borderRadius: 2,
        position: 'relative',
        isolation: 'isolate',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          padding: '1px',
          borderRadius: 'inherit',
          background: 'linear-gradient(135deg, rgba(63,81,181,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          zIndex: -1
        }
      }}
      ref={contentRef}
      id="section-marketTrends"
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Market Trends
          </Typography>

          <IconButton
            onClick={handleClick}
            size="small"
            sx={{
              height: 28,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider'
            }}
            aria-label="download options"
            className="screenshot-controls"
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            className="screenshot-controls"
            slotProps={{
              paper: {
                sx: {
                  border: 1,
                  borderColor: 'secondary.light',
                  borderRadius: 1
                }
              }
            }}
          >
            <MenuItem onClick={downloadAsPng}>Download as PNG</MenuItem>
          </Menu>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Company tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" aria-label="companies tabs">
            {/* All Trends tab */}
            <Tab
              key="all-trends"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CompareArrows sx={{ mr: 1, fontSize: 20 }} />
                  <Typography>All Trends</Typography>
                </Box>
              }
            />

            {/* Individual company tabs */}
            {allCompanies.map((company, index) => (
              <Tab
                key={company.company_name || mainCompanyName}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {company.isMainCompany ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 18,
                          height: 18,
                          bgcolor: 'primary.main',
                          borderRadius: '50%',
                          mr: 1,
                          color: 'white',
                          fontSize: '0.7rem',
                          fontWeight: 'bold'
                        }}
                      >
                        ★
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          bgcolor: getCompetitorColor(index - (hasCompanyMarketTrends ? 1 : 0)),
                          borderRadius: '50%',
                          mr: 1
                        }}
                      />
                    )}
                    <Favicon company={company.isMainCompany ? mainCompanyName : company.company_name} size={20} />
                    <Typography sx={{ ml: 1 }}>{company.isMainCompany ? mainCompanyName : company.company_name}</Typography>
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        {/* All Trends Tab Content */}
        {currentTab === 0 && (
          <Box>
            {/* Key Market Trends */}
            {allTrends.length > 0 && (
              <Box sx={{ mb: 4 }} id="market-trends">
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <TrendingUp sx={{ color: 'primary.main' }} />
                  <Typography variant="h6">Key Market Trends</Typography>
                </Stack>

                <Grid container spacing={3}>
                  {allTrends.map(({ trend }, trendIndex) => (
                    <Grid item xs={12} md={6} key={trendIndex}>
                      <motion.div
                        key={trendIndex}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: trendIndex * 0.1 }}
                      >
                        <Card
                          sx={{
                            height: '100%',
                            borderLeft: 4,
                            borderColor: 'primary.main',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                          }}
                        >
                          <CardContent>
                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                              {trend.title}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              {trend.description}
                            </Typography>

                            {trend.industry_relevance && (
                              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'background.neutral', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  Industry Relevance: {trend.industry_relevance}
                                </Typography>
                              </Box>
                            )}

                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{
                                bgcolor: 'grey.50',
                                py: 1,
                                px: 1.5,
                                borderRadius: 1
                              }}
                            >
                              <MenuBook sx={{ fontSize: 16, color: 'primary.main' }} />
                              <Typography variant="caption" color="text.secondary">
                                Source: {trend.source.document}, p.{trend.source.page}
                              </Typography>
                            </Stack>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Industry Insights/Benchmarks */}
            {allBenchmarks.length > 0 && (
              <Box id="market-benchmarks">
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <BarChart3 sx={{ color: 'success.main' }} />
                  <Typography variant="h6">Industry Insights</Typography>
                </Stack>

                <Grid container spacing={3}>
                  {allBenchmarks.map(({ benchmark }, benchmarkIndex) => (
                    <Grid item xs={12} md={6} key={benchmarkIndex}>
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: benchmarkIndex * 0.1 }}
                      >
                        <Card
                          sx={{
                            height: '100%',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'all 0.3s',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
                            }
                          }}
                        >
                          <CardContent>
                            {/* Decorative Elements */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: 128,
                                height: 128,
                                bgcolor: 'success.main',
                                opacity: 0.1,
                                borderBottomLeftRadius: '100%'
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                width: 96,
                                height: 96,
                                bgcolor: 'success.main',
                                opacity: 0.1,
                                borderTopRightRadius: '100%'
                              }}
                            />

                            <Stack spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
                              <Stack direction="row" spacing={2} alignItems="center">
                                <Box
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    background: (theme) =>
                                      `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  {getIconForMetric(benchmark.metric_name)}
                                </Box>

                                <Box>
                                  <Typography variant="h4" sx={{ lineHeight: 1.2 }}>
                                    {benchmark.metric_value}
                                  </Typography>
                                  <Typography variant="subtitle1">{benchmark.metric_name}</Typography>
                                </Box>
                              </Stack>

                              <Typography variant="body2">{benchmark.description}</Typography>

                              {benchmark.industry_relevance && (
                                <Box sx={{ p: 1.5, bgcolor: 'background.neutral', borderRadius: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    Industry Relevance: {benchmark.industry_relevance}
                                  </Typography>
                                </Box>
                              )}

                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="flex-start"
                                sx={{
                                  bgcolor: 'background.paper',
                                  py: 1,
                                  px: 1.5,
                                  borderRadius: 1,
                                  border: 1,
                                  borderColor: 'grey.100',
                                  flexWrap: 'wrap'
                                }}
                              >
                                <MenuBook sx={{ fontSize: 16, color: 'success.main', mt: 0.5 }} />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    wordBreak: 'break-word',
                                    flex: 1
                                  }}
                                >
                                  Source: {benchmark.source.document}, p.{benchmark.source.page}
                                </Typography>
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {allTrends.length === 0 && allBenchmarks.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No market trends data available.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Individual company content */}
        {allCompanies.map((company, index) => {
          // Add +1 to index to account for the "All Trends" tab
          if (currentTab !== index + 1) return null;

          const marketTrends = getNestedProperty(company, 'analysis_data.market_trends');
          const trends = marketTrends?.trends || [];
          const benchmarks = marketTrends?.benchmarks || [];
          const industry = marketTrends?.industry || '';
          const color = company.isMainCompany ? 'primary.main' : getCompetitorColor(index - (hasCompanyMarketTrends ? 1 : 0));
          const displayName = company.isMainCompany ? mainCompanyName : company.company_name;

          return (
            <Box key={displayName}>
              {industry && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.neutral', borderRadius: 1 }}>
                  <Typography variant="h6">
                    Industry: <span style={{ fontWeight: 600 }}>{industry}</span>
                  </Typography>
                </Box>
              )}

              {/* Key Market Trends */}
              {trends.length > 0 && (
                <Box sx={{ mb: 4 }} id="market-trends">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <TrendingUp sx={{ color: color }} />
                    <Typography variant="h6">Key Market Trends</Typography>
                  </Stack>

                  <Grid container spacing={3}>
                    {trends.map((trend, trendIndex) => (
                      <Grid item xs={12} md={6} key={trendIndex}>
                        <motion.div
                          key={trendIndex}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: trendIndex * 0.1 }}
                        >
                          <Card
                            sx={{
                              height: '100%',
                              borderLeft: 4,
                              borderColor: color,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                            }}
                          >
                            <CardContent>
                              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                {trend.title}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 2 }}>
                                {trend.description}
                              </Typography>

                              {trend.industry_relevance && (
                                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'background.neutral', borderRadius: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    Industry Relevance: {trend.industry_relevance}
                                  </Typography>
                                </Box>
                              )}

                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{
                                  bgcolor: 'grey.50',
                                  py: 1,
                                  px: 1.5,
                                  borderRadius: 1
                                }}
                              >
                                <MenuBook sx={{ fontSize: 16, color: color }} />
                                <Typography variant="caption" color="text.secondary">
                                  Source: {trend.source.document}, p.{trend.source.page}
                                </Typography>
                              </Stack>
                            </CardContent>
                          </Card>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Industry Insights/Benchmarks */}
              {benchmarks.length > 0 && (
                <Box id="market-benchmarks">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <BarChart3 sx={{ color: color }} />
                    <Typography variant="h6">Industry Insights</Typography>
                  </Stack>

                  <Grid container spacing={3}>
                    {benchmarks.map((benchmark, benchmarkIndex) => (
                      <Grid item xs={12} md={6} key={benchmarkIndex}>
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: benchmarkIndex * 0.1 }}
                        >
                          <Card
                            sx={{
                              height: '100%',
                              position: 'relative',
                              overflow: 'hidden',
                              transition: 'all 0.3s',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
                              }
                            }}
                          >
                            <CardContent>
                              {/* Decorative Elements */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  right: 0,
                                  width: 128,
                                  height: 128,
                                  bgcolor: color,
                                  opacity: 0.1,
                                  borderBottomLeftRadius: '100%'
                                }}
                              />
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: 0,
                                  left: 0,
                                  width: 96,
                                  height: 96,
                                  bgcolor: color,
                                  opacity: 0.1,
                                  borderTopRightRadius: '100%'
                                }}
                              />

                              <Stack spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 2,
                                      background: `${color}`,
                                      color: 'white',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    {getIconForMetric(benchmark.metric_name)}
                                  </Box>

                                  <Box>
                                    <Typography variant="h4" sx={{ lineHeight: 1.2 }}>
                                      {benchmark.metric_value}
                                    </Typography>
                                    <Typography variant="subtitle1">{benchmark.metric_name}</Typography>
                                  </Box>
                                </Stack>

                                <Typography variant="body2">{benchmark.description}</Typography>

                                {benchmark.industry_relevance && (
                                  <Box sx={{ p: 1.5, bgcolor: 'background.neutral', borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      Industry Relevance: {benchmark.industry_relevance}
                                    </Typography>
                                  </Box>
                                )}

                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="flex-start"
                                  sx={{
                                    bgcolor: 'background.paper',
                                    py: 1,
                                    px: 1.5,
                                    borderRadius: 1,
                                    border: 1,
                                    borderColor: 'grey.100',
                                    flexWrap: 'wrap'
                                  }}
                                >
                                  <MenuBook sx={{ fontSize: 16, color: color, mt: 0.5 }} />
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      wordBreak: 'break-word',
                                      flex: 1
                                    }}
                                  >
                                    Source: {benchmark.source.document}, p.{benchmark.source.page}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </CardContent>
                          </Card>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {trends.length === 0 && benchmarks.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No market trends data available for this company.
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default CompaniesMarketTrends;
